import React from 'react';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import AxLoungeBanner from '@/components/Social/@components/AxLoungeBanner';
import { useGetBoardDetail } from '@/hooks/apiHooks';
import { PAGE_URL, SOCIAL_SNB_BANNER_BOARD_ID } from '@/constant/url';
import { getCloudflareResizedImageUrl, imgUrlChk } from '@/utils/function';
import { useSocialEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { SocialEventName } from '@/constant/gtm';

/** AX Lounge 배너 영상(1200x900) 비율 — 모든 슬라이드 높이를 여기에 맞춘다. */
const BANNER_ASPECT_RATIO = '4 / 3';
/** 게시글에 대표 이미지가 없을 때 쓰는 기본 배너 이미지 */
const FALLBACK_IMAGE = '/images/board_empty_image.png';
/**
 * 배너 노출 폭이 소셜 SNB 356px / 홈 SNB 371px 라 원본을 그대로 받을 필요가 없다.
 * 고해상도 대응으로 넉넉히 2배(760px)만 요청한다.
 */
const BANNER_IMAGE_RESIZE_OPTIONS =
  'width=760,quality=80,format=auto,fit=scale-down';

/** SNB 배너 캐러셀 틀. 홈 TIP · 회의록 필터 배너(ContentBannerCarousel)도 같은 모양을 쓴다. */
export const SNB_BANNER_FRAME_SX = {
  position: 'relative',
  width: '100%',
  aspectRatio: BANNER_ASPECT_RATIO,
  overflow: 'hidden',
  '.swiper, .swiper-slide': { width: '100%', height: '100%' },
  '.swiper-pagination': {
    filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.45))',
  },
  '--swiper-pagination-bottom': '6px',
  '--swiper-pagination-bullet-size': '5px',
  '--swiper-pagination-bullet-horizontal-gap': '3px',
  '--swiper-pagination-color': '#FFFFFF',
  '--swiper-pagination-bullet-inactive-color': '#FFFFFF',
  '--swiper-pagination-bullet-inactive-opacity': '0.45',
} as const;

type P = {
  /** 게시글 상세 모달을 여는 핸들러. 없으면 상세 페이지로 이동한다. */
  onBoardClick?: (documentId: string) => void;
};

type ImageSlideProps = {
  src: string;
  /** src 로드 실패 시 한 번만 되돌릴 원본. 없으면 되돌리지 않는다. */
  fallbackSrc?: string;
  alt?: string;
  onClick: () => void;
};

/** 클릭/키보드로 열리는 이미지 슬라이드. 이미지가 없으면 기본 이미지를 가운데 둔다. */
export const ImageSlide = ({
  src,
  fallbackSrc,
  alt,
  onClick,
}: ImageSlideProps) => (
  <Box
    role={'button'}
    tabIndex={0}
    aria-label={alt}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onClick();
    }}
    sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      backgroundColor: '#F0F0F0',
      // 대표 이미지가 없으면 기본 이미지를 늘리지 않고 가운데 배치한다.
      ...(src
        ? undefined
        : {
            backgroundImage: `url(${FALLBACK_IMAGE})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center 42%',
            backgroundSize: '96px auto',
          }),
    }}
  >
    {src && (
      <Box
        component={'img'}
        src={src}
        alt={alt}
        // SNB 최상단이라 첫 화면에 바로 보인다. 지연 로딩하지 않는다.
        loading={'eager'}
        draggable={false}
        // 리사이즈 URL 이 실패하면 원본으로 한 번만 되돌린다.
        onError={(event) => {
          if (!fallbackSrc) return;
          const image = event.currentTarget;
          if (image.dataset.fallbackApplied) return;
          image.dataset.fallbackApplied = 'true';
          image.src = fallbackSrc;
        }}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    )}
  </Box>
);

/**
 * 소셜 SNB 상단 배너 캐러셀.
 * 1번 슬라이드는 지정 게시글의 대표 이미지, 2번 슬라이드는 기존 AX Lounge 영상이다.
 * 자동 전환은 두지 않고 스와이프/페이지네이션 클릭으로만 넘긴다.
 */
const SnbBannerCarousel = ({ onBoardClick }: P) => {
  const router = useRouter();
  const eventTracker = useSocialEventTracker();
  const { data: featuredBoard, error } = useGetBoardDetail(
    SOCIAL_SNB_BANNER_BOARD_ID,
    {
      revalidateOnFocus: false,
    },
  );

  const openBoard = (documentId: string, contents: string) => {
    eventTracker(SocialEventName.clickEvent, {
      area_name: 'Social 목록',
      contents,
      status: undefined,
      description: 'SNB 배너',
    });

    if (onBoardClick) {
      onBoardClick(documentId);
      return;
    }
    router.push(PAGE_URL.BOARD_DETAIL(documentId));
  };

  const featuredImage =
    featuredBoard?.files?.thumbnails?.[0]?.link ||
    featuredBoard?.files?.slideImages?.[0]?.link;
  const hasFeatured = Boolean(featuredBoard) && !error;

  return (
    <Box sx={SNB_BANNER_FRAME_SX}>
      <Swiper
        slidesPerView={1}
        spaceBetween={0}
        simulateTouch={true}
        grabCursor={true}
        modules={[Pagination]}
        pagination={hasFeatured ? { clickable: true } : false}
      >
        {hasFeatured && (
          <SwiperSlide>
            <ImageSlide
              src={
                featuredImage
                  ? getCloudflareResizedImageUrl(
                      imgUrlChk(featuredImage),
                      BANNER_IMAGE_RESIZE_OPTIONS,
                    )
                  : ''
              }
              fallbackSrc={featuredImage ? imgUrlChk(featuredImage) : undefined}
              alt={featuredBoard?.subject}
              onClick={() =>
                openBoard(
                  SOCIAL_SNB_BANNER_BOARD_ID,
                  featuredBoard?.subject ?? '',
                )
              }
            />
          </SwiperSlide>
        )}
        <SwiperSlide>
          <AxLoungeBanner />
        </SwiperSlide>
      </Swiper>
    </Box>
  );
};

export default SnbBannerCarousel;
